import { useEffect, type RefObject } from "react";
import {
  getShouldDismissTooltipFromKey,
  getShouldDismissTooltipFromMouseEvent,
} from "./ctooltip-selectors";

interface UseTooltipDismissalInput {
  anchorElement: HTMLElement | null;
  isOpen: boolean;
  onDismiss?: () => void;
  tooltipRef: RefObject<HTMLDivElement | null>;
}

export function useTooltipDismissal({
  anchorElement,
  isOpen,
  onDismiss,
  tooltipRef,
}: UseTooltipDismissalInput): void {
  const hasDismissBinding = isOpen && Boolean(onDismiss);

  useEffect(() => {
    if (!hasDismissBinding || !onDismiss) {
      return;
    }

    const handleClickOutside = (event: MouseEvent): void => {
      const shouldDismiss = getShouldDismissTooltipFromMouseEvent({
        anchorElement,
        event,
        tooltipElement: tooltipRef.current,
      });

      if (shouldDismiss) {
        onDismiss();
      }
    };

    const handleKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (getShouldDismissTooltipFromKey({ key: event.key })) {
        onDismiss();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorElement, hasDismissBinding, onDismiss, tooltipRef]);
}
